import { useState, useCallback } from "react";
import {
  sendUpgradeEmail,
  completeUpgradeLink,
  completeLoginLink,
  type AuthLinkMode,
} from "@/lib/firebase";
import type { User } from "firebase/auth";

export type UpgradeStep = "idle" | "email" | "sending" | "sent" | "completing" | "done" | "error";

interface UpgradeState {
  step: UpgradeStep;
  email: string;
  mode: AuthLinkMode;
  error: string | null;
}

export function useAccountUpgrade() {
  const [state, setState] = useState<UpgradeState>({
    step: "idle",
    email: "",
    mode: "upgrade",
    error: null,
  });

  const setEmail = useCallback((email: string) => {
    setState((prev) => ({ ...prev, email, error: null }));
  }, []);

  const reset = useCallback(() => {
    setState({ step: "idle", email: "", mode: "upgrade", error: null });
  }, []);

  /** Step 1: Send the magic-link email. Returns the detected mode on success. */
  const sendLink = useCallback(async (email: string): Promise<AuthLinkMode | false> => {
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setState((prev) => ({ ...prev, error: "Please enter a valid email address" }));
      return false;
    }

    setState((prev) => ({ ...prev, step: "sending", error: null }));

    try {
      const { mode } = await sendUpgradeEmail(email.trim());
      setState({ step: "sent", email: email.trim(), mode, error: null });
      return mode;
    } catch (err: any) {
      const message =
        err.code === "auth/invalid-email"
          ? "Invalid email address"
          : err.code === "auth/missing-continue-uri"
            ? "Configuration error — please try again later"
            : err.message || "Failed to send the magic link";
      setState((prev) => ({ ...prev, step: "error", error: message }));
      return false;
    }
  }, []);

  /**
   * Step 2: Complete the link on the /complete-upgrade or /complete-login page.
   * @param mode Which completion action to take. Defaults to 'upgrade' for backward compat.
   */
  const completeLink = useCallback(
    async (
      url: string,
      syncToBackend: (user: User) => Promise<void>,
      mode: AuthLinkMode = "upgrade",
    ): Promise<User | null> => {
      setState((prev) => ({ ...prev, step: "completing", mode, error: null }));

      try {
        const user =
          mode === "login"
            ? await completeLoginLink(url)
            : await completeUpgradeLink(url);

        // Sync the updated user to the backend
        await syncToBackend(user);

        setState({ step: "done", email: user.email || "", mode, error: null });
        return user;
      } catch (err: any) {
        const message =
          err.code === "auth/credential-already-in-use"
            ? "This email is already linked to another account. Try signing in instead."
            : err.code === "auth/invalid-action-code"
              ? "This link has expired or was already used. Please request a new one."
              : err.code === "auth/email-already-in-use"
                ? "This email is already linked to another account."
                : err.message || "Failed to complete sign-in";
        setState((prev) => ({ ...prev, step: "error", error: message }));
        return null;
      }
    },
    [],
  );

  return {
    ...state,
    setEmail,
    sendLink,
    completeLink,
    reset,
    isPending: state.step === "sending" || state.step === "completing",
  };
}
